import type { ReactNode } from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { AnswersPanel, MenuSubsPanels, MessagesPanel } from '@/deskUiSkeletons/Page/Panels'
import { EnterPinForm, LoadingOverlay, QuickReply, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { ForumEditorTemplate } from './editor/ForumEditorTemplate'
import { ForumMainAttachments } from './ForumMainAttachments'
import { ForumMainTopic } from './ForumMainTopic'
import { ForumMenu } from './ForumMenu'
import { ForumParentTrail } from './ForumParentTrail'

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const { state } = useSubsStore()
  const info = workspaceByKind[kind]
  const visible = state.chromeByContract[info.contract]?.visible !== false
  return (
    <section {...legacyHookAttrs(info.contract, `md-forum-workspace md-forum-workspace--${kind} min-w-0${visible ? '' : ' hidden'}${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}`)} style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }} {...(visible ? {} : { 'data-md-react-display': 'none' })}>{children}</section>
  )
}

function RouteHeading({ title, detail }: { title: string; detail: string }) {
  return (
    <header className="mb-2 border border-[#006699] font-[Verdana,Arial,Helvetica,sans-serif] text-[11px]">
      <h1 className="bg-[#006699] px-2 py-1 font-bold text-white">{title}</h1>
      <p className="bg-[#eef3f8] px-2 py-1 text-[10px] text-black">{detail}</p>
    </header>
  )
}

export function ForumPage({ isEditorOpen = false, editorMode, settingsOpen, onToggleSettings, onCloseSettings }: {
  isEditorOpen?: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
  onCloseSettings: () => void
}) {
  const { state } = useSubsStore()
  const mode = state.renderProjection.mode
  const editorActive = isEditorOpen || mode === 'editor'

  return (
    <>
      <ShellOverlays />
      <div {...legacyHookAttrs('page.root', 'md-forum-page min-h-screen w-full bg-[#e5e5e5] font-[Verdana,Arial,Helvetica,sans-serif] text-black')}>
        <div {...legacyHookAttrs('page.table', 'md-forum-page-table min-h-screen w-full')}>
          <div {...legacyHookAttrs('page.header', 'h-12')}><div {...legacyHookAttrs('page.headerCell', 'h-12')} /></div>
          <div className="mx-auto flex w-full max-w-[1180px]">
            <div {...legacyHookAttrs('page.accentCell', 'hidden')} hidden aria-hidden="true" />
            <main {...legacyHookAttrs('page.workspaceCell', 'min-w-0 flex-1 bg-white px-2 pb-8 pt-2')}>
              <WorkspaceSection kind="main">
                {mode === 'msg' ? <><RouteHeading title="Forum topic" detail="Topic body and replies. Other forum sections are available from the menu." /><ForumMainTopic /><ForumParentTrail /><AnswersPanel /></> : null}
                {mode === 'answers' ? <><RouteHeading title="Replies" detail="All answers to this forum topic." /><AnswersPanel /></> : null}
                {mode === 'submsges' ? <><RouteHeading title="Topics" detail="Related messages are kept off the topic root." /><MessagesPanel /></> : null}
              </WorkspaceSection>
              <WorkspaceSection kind="editor"><ForumEditorTemplate active={editorActive} editorMode={isEditorOpen ? editorMode : ''} settingsOpen={settingsOpen} onCloseSettings={onCloseSettings} /></WorkspaceSection>
              <WorkspaceSection kind="attachments"><ForumMainAttachments /></WorkspaceSection>
              <WorkspaceSection kind="favs"><RouteHeading title="Bookmarks" detail="Saved forum replies and topics." /><MenuSubsPanels mode="favs" /></WorkspaceSection>
              <WorkspaceSection kind="trash"><RouteHeading title="Basket" detail="Removed forum replies and topics." /><MenuSubsPanels mode="trash" /></WorkspaceSection>
              <WorkspaceSection kind="drafts"><RouteHeading title="Drafts" detail="Unpublished forum replies and topics." /><MenuSubsPanels mode="drafts" /></WorkspaceSection>
            </main>
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} hidden aria-hidden="true" />
          </div>
          <footer {...legacyHookAttrs('page.footer', 'mx-auto max-w-[1180px] border-t border-[#006699] bg-[#eef3f8] px-2 py-2 text-center text-[9px] text-[#006699]')}>MsgDesk Forum</footer>
        </div>
      </div>
      <ForumMenu isEditorOpen={editorActive} editorMode={editorMode} settingsOpen={settingsOpen} onToggleSettings={onToggleSettings} />
      <QuickReply />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </>
  )
}
